import React from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import Draggable from 'react-draggable';

import { backgroundImage, getTypeFromUrl } from 'utils/media';
import { rectDiff, gridPosition } from 'utils/geometry';
import { FileType } from 'constants/constants';

import { getSceneStyle, compareSettings } from 'store/models/Scene';

import { IconButton } from 'components/Button';
import * as Icons from 'components/Svg';

// ======================
// HELPERS
// ====================== 


// ======================
// PARTIALS
// ====================== 
class SceneCard extends React.Component {
  _wrapperRef = null
  onWrapperRef = n => this._wrapperRef = n
  _video = React.createRef()

  _getBreakLength = () => {
    let { settings, defaultSettings, index } = this.props;
    let length = index === 0
      ? defaultSettings?.pause || settings?.slideBreak
      : settings?.slideBreak;
    return length;
  }

  static defaultProps = {
    interval: 5000,
  }

  state = {
    timer: null,
    start: null,
    position: null,
    breakCount: null,
    dragging: false,
    dragPosition: {x: 0, y: 0},
  }

  componentDidUpdate(prevProps, prevState) {
    let { playing, breaking } = this.props;
    if(prevProps.playing && !playing) {
      this.stop();
    } else if(!prevProps.playing && playing) {
      this.play();
    }
    if(!prevProps.breaking && breaking)
      this.startBreak();
  }

  componentDidMount() {
    if(this.props.playing)
      this.play();
  }

  componentWillUnmount() {
    let { timer } = this.state;
    clearInterval(timer);
  }

  play = () => {
    let { timer } = this.state;
    clearInterval(timer);
    timer = setInterval(this.onTimerTick, 100);
    this.setState({
      timer,
      start: Date.now(),
      position: 0,
      breaking: false,
    })
  }

  stop = () => {
    let { timer } = this.state;
    clearInterval(timer);
    this.setState({
      timer: null,
      start: null,
      position: null,
    });
  }

  onTimerTick = () => {
    let { settings } = this.props;
    let { start } = this.state;
    let length = settings.length;
    let diff = Date.now() - start;
    let position = diff / length;

    if(diff >= length) {
      if(this.props.playing) {
        this.setState({ 
          start: Date.now(),
          position: 0 })
      }
    } else {
      this.setState({ position, });
    }
  }

  startBreak = () => {
    let { settings } = this.props;
    let { timer } = this.state;
    clearInterval(timer);
    timer = setInterval(this.onBreakTick, 1000);
    let breakLength = this._getBreakLength();
    let breakCount = breakLength / 1000;
    this.setState({ 
      timer, 
      start: Date.now(), 
      breakCount 
    })
  }

  onBreakTick = () => {
    let { start } = this.state;
    let breakLength = this._getBreakLength();
    let breakCount = breakLength - (Date.now() - start)  
      |> # / 1000
      |> Math.round
      |> Math.max(#,0);
    this.setState({ breakCount });
  }

  onDragStart = (e, data) => {
    e.stopPropagation();
    e.preventDefault();
  }

  onDrag = (e, data) => {
    e.stopPropagation();
    e.preventDefault();

    let { onDragStart, scene } = this.props;
    let { dragging } = this.state;

    if(!dragging && (Math.abs(data.x) > 5 || Math.abs(data.y) > 5)) {
      this.setState({ dragging: true });
      data = _.set(data, 'parent', this._wrapperRef);
      onDragStart ? onDragStart(data, scene._id) : null;
    } else if(dragging) {
      data = _.set(data, 'parent', this._wrapperRef);
      this.props.onDrag ? this.props.onDrag(data) : null;
    }
  }

  onDragStop = (e, data) => {
    let { onDragStop, scene } = this.props;
    let { dragging } = this.state;
    
    if(!dragging)
      return;

    this.setState({ dragPosition: { x: 0, y: 0 }, });
    data = _.set(data, 'parent', this._wrapperRef);
    onDragStop?.(data, scene._id);
  }

  onImageClick = e => {
    if(this.state.dragging)
      return this.setState({ dragging: false });

    let { onClick, scene } = this.props;
    onClick?.(scene);
  }

  onVideoLoad = () => {
    this._video.current.currentTime = (this._video.current.duration ?? 0) * .10;
  }

  render() {
    let { scene, selected, disabled, playing, breaking, enableSort, highlighted, defaultSettings, dragTarget, settings, index, onClick } = this.props;
    let { timer, breakCount, position, dragging, dragPosition } = this.state;
    let length = settings?.length;
    let style = getSceneStyle(settings);
    let custom = !compareSettings(settings, defaultSettings);
    
    let type = getTypeFromUrl(scene.url);
    let thumbnail;
    switch(type) {
      case FileType.VIDEO:
        thumbnail = (
          <div className="thumb video">
            <video controls={false} ref={this._video} onLoadedData={this.onVideoLoad}>
              <source src={scene.url} />
            </video>
          </div>
        );
        break;
      default:
        thumbnail = <div className="thumb image" style={backgroundImage(scene.url)} />;
    }

    return (
      <div ref={this.onWrapperRef} className={cx("scenecard-wrapper", { 
        clickable: !_.isNil(onClick) || enableSort, 
        dragging, 
        disabled, 
        highlighted, 
        'drag-target': dragTarget })}>
        <Draggable 
          onStart={this.onDragStart}
          onStop={this.onDragStop}
          onDrag={this.onDrag}
          position={dragPosition}
          disabled={disabled || !enableSort}>
          <div 
            className={cx("scenecard", { selected, playing, breaking, 'loop-break': breaking && index === 0, custom })} 
            style={style} 
            onClick={this.onImageClick}>
            { thumbnail }
            { playing &&
            <div className="playhead" style={{ width: `${position * 100}%`}}></div>}
            { length && 
            <div className="length">{ length / 1000 }s</div>}
            { custom && !disabled &&
            <div className="custom"><Icons.AsteriskIcon color="black" /></div>}
            { breaking &&
            <div className={cx("slide-break", { 'loop-break': index === 0, breaking, playing })}>
              <Icons.PauseIcon size="xs" color="dark" />
              <span className="break-length">{ breakCount }s</span>
            </div>}
            { /* <div className="id">{scene._id}</div> */ }
          </div>
        </Draggable>
        <div className="ghost-image"><div style={backgroundImage(scene.url)}></div></div>
      </div>
    );
  }
}

const UploadCard = p => {
  return (
    <div className="scenecard-wrapper upload" onClick={e => p.onUploadClick?.()}>
      <div className="uploadcard"><Icons.PlusIcon /></div>
    </div>);
}


// ======================
// COMPONENT
// ====================== 
class SceneStrip extends React.Component {
  _markerRef = null
  _listRef = null
  onMarkerRef = n => this._markerRef = n
  onListRef = n => this._listRef = n

  static defaultProps = {
    interval: 5000,
  }

  state = {
    scenes: [],
    uploading: false,
    completed: false,
    progress: 0,
    dragging: false,
    order: { row: 0, column: 0 },
    overIndex: null,
  }

  onSceneClick = scene => this.props.onSceneSelect?.(scene?._id, scene);
  
  onDragStart = data => {
    this.setState({ dragging: true });
    this.props.onDragStart?.();
  }
  onUploadClick = () => this.props.onUpload?.()

  onDrag = (data) => {
    let { scenes, onDragStart } = this.props;
    
    let parentRect = data.parent.getBoundingClientRect();
    let elmRect = data.node.getBoundingClientRect();
    let contRect = this._listRef.getBoundingClientRect();

    let diff = rectDiff(contRect, elmRect);
    let order = gridPosition(diff.x, diff.y, parentRect.width, parentRect.height, contRect.width, contRect.height);
    let overIndex = (order.columnCount * (order.row - 1)) + (order.column - 1);
    overIndex = Math.min(_.size(scenes) - 1, overIndex);
    this.setState({ order, overIndex });
  }

  onDragStop = (data, id) => {
    let { scenes, onOrderChange, disabledIds } = this.props;
    let { order, overIndex } = this.state;

    let sceneId = scenes?.[overIndex]?._id;
    if(sceneId && _.includes(disabledIds, sceneId))
      return;
    
    this.setState({ dragging: false, overIndex: null });
    onOrderChange?.(id, overIndex);
  }

  render() {
    let { 
      scenes, 
      disabledIds, 
      selectedIndex, 
      playingIndex,
      breakIndex,
      overlaySettings, 
      sceneSettings, 
      playing,
      enableSort = true,
      showAddScene = true, } = this.props;
    let { dragging, overIndex } = this.state;
    return (
    <div className="g-scene-strip">
      { (scenes && scenes.length > 0) &&
      <div ref={this.onListRef} className="scenecard-list">
        { _.map(scenes, (scene, i) => {
          let disabled = _.includes(disabledIds, scene._id);
          let selected = (selectedIndex === i);
          let isPlaying = (playingIndex === i) && playing;   
          let isBreaking = ((breakIndex === i) && playing);
          let highlighted = !_.isEmpty(disabledIds) && !_.includes(disabledIds, scene._id); 
          let dragTarget = !disabled && dragging && overIndex === i;
          let settings = sceneSettings?.[scene._id]?.settings ?? {};
          return (
            <SceneCard
              key={scene._id}
              scene={scene}
              defaultSettings={overlaySettings}
              settings={settings}
              playing={isPlaying}
              breaking={isBreaking}
              selected={selected}
              disabled={disabled}
              highlighted={highlighted}
              index={i}
              dragTarget={dragTarget}
              onClick={this.onSceneClick}
              onDragStart={this.onDragStart}
              onDrag={this.onDrag}
              onDragStop={this.onDragStop} />)
        })}
        {showAddScene && 
        <UploadCard onUploadClick={this.onUploadClick} />}
      </div>}
    </div>);
  }
}

  export default SceneStrip;